import React from 'react';
import cx from 'classnames';

import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import FAQSection from '@marketing/sections/faq';
import { CurrentPlanProps, OfferProps } from '@marketing/types';
import { defaultRegistrationUrl } from '@marketing/utilities';

import ApplicationSupportSection from './sections/application_support';
import SocialProofSection from './sections/social_proof';
import TestimonialsSection from './sections/testimonials';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import HeroSection from '../sections/hero';
import OffersSection from '../sections/offers';
import StartYourPrepSection from '../sections/start_your_prep';

import OfficialQuestionsIcon from '@marketing/sections/value_props/assets/green/banner_check_icon.svg';
import PracticeQuestionsIcon from '@marketing/sections/value_props/assets/green/practice_questions_icon.svg';
import PracticeTestsIcon from '@marketing/sections/value_props/assets/green/practice_tests_icon.svg';
import ScorePredictorIcon from '@marketing/sections/value_props/assets/green/score_gauge_icon.svg';
import StudySchedulesIcon from '@marketing/sections/value_props/assets/green/timely_guidance_icon.svg';
import VideoLessonsIcon from '@marketing/sections/value_props/assets/green/video_lessons_icon.svg';

import BannerCheckIcon from '../sections/competitor_comparison/assets/banner_check_icon.svg';
import BubbleQuestionIcon from '../sections/competitor_comparison/assets/bubble_question_icon.svg';
import ChecklistIcon from '../sections/competitor_comparison/assets/checklist_icon.svg';
import CircleRefreshIcon from '../sections/competitor_comparison/assets/circle_refresh_icon.svg';
import DollarSignIcon from '../sections/competitor_comparison/assets/dollar_sign_icon.svg';
import FireworkIcon from '../sections/competitor_comparison/assets/firework_icon.svg';
import FlashcardsIcon from '../sections/competitor_comparison/assets/flashcards_icon.svg';
import HighFiveIcon from '../sections/competitor_comparison/assets/high_five_icon.svg';
import HourglassIcon from '../sections/competitor_comparison/assets/hourglass_icon.svg';
import MobileDeviceIcon from '../sections/competitor_comparison/assets/mobile_device_icon.svg';
import NotebookIcon from '../sections/competitor_comparison/assets/notebook_icon.svg';
import ScoreGaugeIcon from '../sections/competitor_comparison/assets/score_gauge_icon.svg';
import StarIcon from '../sections/competitor_comparison/assets/star_icon.svg';
import VideoPlayerIcon from '../sections/competitor_comparison/assets/video_player_icon.svg';

import styles from '../style.module.scss';
import marketingStyles from '@marketing/style.module.scss';

interface Props extends PageProps {
  currentPlan: CurrentPlanProps | null;
  offers: OfferProps[];
}

export default (props: Props) => {
  const admissionsBundleOffer = props.offers.find((offer) => offer.slug === 'premium-admissions-bundle' || offer.slug === 'premium-admissions-bundle-in');
  const premiumOffer = props.offers.find((offer) => offer.slug === 'premium' || offer.slug === 'premium-in');
  const premium30DaysOffer = props.offers.find((offer) => offer.slug === 'premium-30-days' || offer.slug === 'premium-30-days-in');
  const trialOffer = props.offers.find((offer) => offer.slug === '1-week');

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.greenGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />
        <div className={styles.greenEllipse} />
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 60 }}
        dashedBorder
        examName="GRE"
        navigationItems={props.navigationItems}
        notice={props.notice}
      >
        <HeroSection
          examName="GRE"
          header={<>Improve Your GRE<span className={styles.registeredMark}>®</span> Score, Guaranteed!</>}
          subHeader="Prep smart at an affordable price. Fully optimized for the shorter GRE."
          valueProps={[
            {
              description: 'Timed, full-length practice tests from our pool of questions',
              icon: PracticeTestsIcon,
              name: 'Practice Tests'
            },
            {
              description: '8 full sections worth of licensed official GRE® questions from ETS',
              icon: OfficialQuestionsIcon,
              name: 'Official Content'
            },
            {
              description: 'Learn from your mistakes with a video explanation to every question',
              icon: PracticeQuestionsIcon,
              name: '1600+ Practice Questions'
            },
            {
              description: 'Prepare for every section, on any device and 100% online',
              icon: VideoLessonsIcon,
              name: 'Expert Video Lessons'
            },
            {
              description: 'Study checklists to stay motivated, 1 week to 6 months long',
              icon: StudySchedulesIcon,
              name: 'Custom Study Schedules'
            },
            {
              description: 'Be confident when you’re ready with our score predictor tool',
              icon: ScorePredictorIcon,
              name: 'Accurate Score Predictor'
            }
          ]}
        />

        <SocialProofSection />

        <OffersSection
          currentPlan={props.currentPlan}
          primaryOffer={{
            header: (
              <>
                <span className="text-bold text-white">Premium</span>
                {' '}<span style={{ color: '#8E70B2' }}>+</span>{' '}
                <span className="text-bold text-white">Admissions</span>
              </>
            ),
            highlight: <><small>Bundle and save <strong>$854</strong> ($1073 value)</small></>,
            offer: admissionsBundleOffer,
            subHeader: 'Comprehensive application support',
            valuePropGroups: [
              {
                content: [
                  {
                    content: '6 months access to everything in our Premium GRE plan',
                  }
                ],
                header: 'Premium GRE ($894 value)'
              },
              {
                content: [
                  {
                    content: 'Application tracker',
                    tooltip: 'Manage your grad school applications<br />in one central place.'
                  },
                  {
                    content: 'Step-by-step guided essay writing',
                    tooltip: 'Go from blank page to well-written draft<br />with our Guided Writing experience.'
                  },
                  {
                    content: '50+ video workshops',
                    tooltip: 'Learn how to put your best foot forward<br />from a top lineup of Magoosh admissions experts<br />with a range of diverse backgrounds.'
                  },
                  {
                    content: '40+ annotated SOPs and Personal Statements',
                    tooltip: 'Check out actual student writings<br />annotated with expert feedback to help you<br />tell your story in the best light.'
                  },
                  {
                    content: '12 months of access',
                    tooltip: 'Full access to Magoosh Admissions for 12 months.'
                  }
                ],
                header: 'Admissions Support ($179 value)',
                subHeader: 'Demystify graduate applications with people who have been there'
              }
            ],
          }}
          secondaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#9BBCAF' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">6 months</span>
              </>
            ),
            offer: premiumOffer,
            subHeader: 'Save $715 by purchasing 6 months at once',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <><strong>8 full sections</strong> of official GRE® questions</>,
                    tooltip: "Content that you can't currently get anywhere else!"
                  },
                  {
                    content: <><strong>290+</strong> video lessons</>,
                    tooltip: 'Lessons cover every concept tested on the GRE'
                  },
                  {
                    content: <><strong>Over 1600</strong> practice questions</>,
                    tooltip: 'Every question has its own in-depth<br />explanation video'
                  },
                  {
                    content: <>Up to <strong>6 practice tests</strong></>,
                    tooltip: 'Our timed and scored full-length practice<br />tests are generated from your pool of 1600<br />practice questions. We recommend taking 6<br />tests with Magoosh, but you can take more.'
                  },
                  {
                    content: 'Study schedules',
                    tooltip: 'Study schedules and checklists to keep you<br />on track.'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <><strong>6 months</strong> of access</>,
                    tooltip: 'Full access for 6 months'
                  },
                  {
                    content: <><strong>+5 total</strong> score guarantee</>,
                    tooltip: "If you've taken the test before and meet<br />our requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund. See<br />the Frequently Asked Questions below for details."
                  },
                  {
                    content: 'Ask an expert',
                    tooltip: 'Quick email and chat support for when you<br />get stuck or just want some extra help'
                  },
                  {
                    content: 'Pause your plan',
                    tooltip: "Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan's end date will be<br />extended by the paused duration."
                  }
                ],
                header: 'Features'
              }
            ],
          }}
          tertiaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#C6C6C6' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">1 month</span>
              </>
            ),
            offer: premium30DaysOffer,
            subHeader: 'Great option for limited study time',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <><strong>8 full sections</strong> of official GRE® questions</>,
                    tooltip: "Content that you can't currently get anywhere else!"
                  },
                  {
                    content: <><strong>290+</strong> video lessons</>,
                    tooltip: 'Lessons cover every concept tested on the GRE'
                  },
                  {
                    content: <><strong>Over 1600</strong> practice questions</>,
                    tooltip: 'Every question has its own in-depth<br />explanation video'
                  },
                  {
                    content: <>Up to <strong>6 practice tests</strong></>,
                    tooltip: 'Our timed and scored full-length practice<br />tests are generated from your pool of 1600<br />practice questions. We recommend taking 6<br />tests with Magoosh, but you can take more.'
                  },
                  {
                    content: 'Study schedules',
                    tooltip: 'Study schedules and checklists to keep you<br />on track.'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <><strong>1 month</strong> of access</>,
                    tooltip: 'Full access for 1 month'
                  },
                  {
                    content: <><strong>+5 total</strong> score guarantee</>,
                    tooltip: "If you've taken the test before and meet<br />our requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund. See<br />the Frequently Asked Questions below for details."
                  },
                  {
                    content: 'Ask an expert',
                    tooltip: 'Quick email and chat support for when you<br />get stuck or just want some extra help'
                  },
                  {
                    content: 'Pause your plan',
                    tooltip: "Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan's end date will be<br />extended by the paused duration."
                  }
                ],
                header: 'Features'
              }
            ],
          }}
          trialOffer={{
            header: 'Try Magoosh GRE for 7 days',
            offer: trialOffer,
            subHeader: 'Start a free trial with 20+ questions and lessons'
          }}
        />

        <CompetitorComparisonSection
          companyFeatureOfferings={[
            {
              name: 'Magoosh',
              'Price': formatPrice(admissionsBundleOffer.fullPriceInCents, 'usd', true),
              'Length of Online Access': '6 months',
              'Official Content': 'Included',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Video & Text Question Explanations': 'Included',
              'No Questions Asked Money-Back Guarantee': 'Included',
              'Score-Improvement Guarantee': '+5 Point Increase Guarantee',
              'Study Plans': 'Included',
              'Graduate Application Tracker & Resources': 'Included',
              'Flashcards Included': 'Included',
              'Score Predictor': 'Included'
            },
            {
              name: 'Princeton Review',
              'Price': '$499',
              'Length of Online Access': '4 months',
              'Practice Questions and Exams': 'Included',
              'Video Lessons': 'Included',
              'Score-Improvement Guarantee': '+1 Point Increase Guarantee'
            },
            {
              name: 'Kaplan',
              'Price': '$499',
              'Length of Online Access': '6 months',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Video Lessons': 'Included',
              'Score-Improvement Guarantee': '+1 Point Increase Guarantee',
              'Study Plans': 'Included',
              'Flashcards Included': 'Included'
            },
            {
              name: 'Manhattan',
              'Price': '$599',
              'Length of Online Access': '6 months',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included'
            }
          ]}
          features={[
            { icon: DollarSignIcon, name: 'Price' },
            { icon: HourglassIcon, name: 'Length of Online Access' },
            { icon: BannerCheckIcon, name: 'Official Content' },
            { icon: BubbleQuestionIcon, name: 'Practice Questions and Exams' },
            { icon: MobileDeviceIcon, name: 'Mobile App' },
            { icon: FireworkIcon, name: 'Free Trial' },
            { icon: VideoPlayerIcon, name: 'Video Lessons' },
            { icon: NotebookIcon, name: 'Video & Text Question Explanations' },
            { icon: CircleRefreshIcon, name: 'No Questions Asked Money-Back Guarantee' },
            { icon: HighFiveIcon, name: 'Score-Improvement Guarantee' },
            { icon: ChecklistIcon, name: 'Study Plans' },
            { icon: StarIcon, name: 'Graduate Application Tracker & Resources' },
            { icon: FlashcardsIcon, name: 'Flashcards Included' },
            { icon: ScoreGaugeIcon, name: 'Score Predictor' }
          ]}
          registrationUrl={defaultRegistrationUrl(admissionsBundleOffer)}
        />

        <TestimonialsSection />

        <ApplicationSupportSection />

        <FAQSection
          entries={[
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  You can do that! <a href="/register/verbal">Sign up for a Verbal+AWA plan here</a>, or <a href="/register/math">sign up for a Math plan here</a>. Both plans last for 6 months and come with a +2 score guarantee. Please note that these plans do not include practice tests or score predictors.
                </>
              ),
              question: 'Can I sign up for a Verbal+AWA or Math only plan?'
            },
            {
              answer: 'Visa, Mastercard, American Express, Discover, and PayPal. We process our transactions through Paypal, so your credit card info is safe.',
              question: 'What types of payment do you accept?'
            },
            {
              answer: (
                <>
                  <p>If you choose to pay in four monthly installments, your first payment is due at checkout, with the remaining installments charged monthly. You'll receive an email with your payment schedule after checkout. </p><br />
                  <p><strong>Please note:</strong> Installment plans are not subscriptions—payments continue even if you pause your plan, but your access will be extended by the paused duration. By choosing an installment plan, you are committing to paying the full purchase amount unless you request a refund within 7 days of your initial purchase. Pausing your plan does not extend the refund window.</p>
                </>
              ),
              question: 'How do installment payments work?'
            },
            {
              answer: "No. Magoosh plans do not renew or auto-renew. You purchase a plan for a set length of time, and when it ends your access simply expires — there's nothing to cancel. If you'd like more time, you can purchase a new plan.",
              question: 'Do Magoosh plans renew?'
            },
            {
              answer: 'Yes we do! You can access all of the lessons anywhere with our apps, and you can use your mobile browser to get full access to the site.',
              question: 'Do you have iPad/iPhone/Android access?'
            },
            {
              answer: "We're happy to help you with questions you have about any of our material. Send us a message while you're studying, and we'll get back to you quickly. We do have limitations to how much we can help any individual student, but in most cases (for 99% of students), we can answer all of your questions!",
              question: "If I'm struggling with a question, can I get help?"
            },
            {
              answer: (
                <>
                  You definitely can! Email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll help set you up.
                </>
              ),
              question: 'Can I purchase multiple accounts for my students or non-profit?'
            },
            {
              answer: (
                <>
                  Yes! <a href="/shorter-gre" target="_blank">The GRE became shorter in 2023</a>. Our content is fully optimized for the shorter GRE. Our video lessons and practice tests have been updated to reflect what you'll see on test day.
                </>
              ),
              question: 'Can I use Magoosh to study for the shorter GRE?'
            },
            {
              answer: 'Yes! The premium plan includes all of our math, verbal, and AWA content.',
              question: 'Does the premium plan include everything from the math and verbal plans?'
            },
            {
              answer: 'Our AI grades your essay like a real GRE grader and then provides feedback to boost your score.  You also get expert-led lessons with strategies and tips for writing a high-scoring essay.',
              question: 'What about the writing section and essay grading?'
            },
            {
              answer: 'All prices are in USD (United States Dollars).',
              question: 'What currency are the prices listed in?'
            },
            {
              answer: "It's 100% online. No DVDs. No books. It's all online, so you can practice taking the test on a computer, just like you'll have to do on exam day for the real GRE. It works on both Macs and PCs: any device with an internet connection!",
              question: 'How does the course work?'
            },
            {
              answer: "With Magoosh Premium, you'll have access to full-length practice tests that accurately reflect what you'll see on test day, including an ungraded writing section. The tests are generated from your pool of unanswered questions so we recommend creating up to 6 practice tests, and saving the remaining for practice.",
              question: 'Do I get practice tests?'
            },
            {
              answer: "No! Many of our students are first-time GRE test-takers, so if you're ready to start studying now, you should go ahead and sign up! Our lessons start from the beginning and teach you everything you need to know about the GRE, so taking the test prior is not required.",
              question: 'Do I need to have taken the test before?'
            },
            {
              answer: 'We know that everyone has a different approach to learning, so we offer study schedules that are flexible enough to fit your own schedule, so you can spend more time learning what you need to.',
              question: 'What are your study schedules like?'
            },
            {
              answer: 'Extremely close. Our experienced instructors use metrics to ensure the difficulty and types of questions match those on the actual test. We rank the questions by difficulty to help you master the basics and progress towards your target score.',
              question: 'How close is the material to the actual GRE?'
            }
          ]}
        />

        <StartYourPrepSection
          examName="GRE"
          pointIncrease={5}
          primaryOffer={{ ...admissionsBundleOffer, name: 'GRE + Admissions' }}
          secondaryOffer={{ ...premiumOffer, name: 'GRE Only' }}
          trialOffer={trialOffer}
        />
      </PageLayout>
    </div>
  );
};
